import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { KineticLines, Overline } from "@/components/motion";
import { useSiteContent } from "@/lib/content";

const PageHero = ({ image, overline, titleLines, subtitle, children, testId = "page-hero", contentKey }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const c = useSiteContent();

  // Surcharges éditables depuis l'admin (page Contenus) : une ligne de titre par ligne de texte
  // (1ère ligne blanche, 2ème en or, suivantes blanches)
  const finalOverline = contentKey ? c(`${contentKey}.overline`, overline) : overline;
  const finalSubtitle = contentKey ? c(`${contentKey}.subtitle`, subtitle) : subtitle;
  const titleOverride = contentKey ? c(`${contentKey}.title`, "") : "";
  const finalTitleLines = titleOverride
    ? titleOverride
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((line, i) => (
          <span key={i} className={i === 1 ? "text-gold" : "text-white"}>{line}</span>
        ))
    : titleLines;

  return (
    <section ref={ref} data-testid={testId} className="relative min-h-[75vh] flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img src={image} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse 60% 50% at 30% 80%, rgba(212,175,55,0.12), transparent)" }}
      />
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pb-20 pt-44">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <Overline>{finalOverline}</Overline>
        </motion.div>
        <KineticLines
          lines={finalTitleLines}
          className="mt-5 font-display uppercase text-6xl sm:text-7xl lg:text-[7.5vw] leading-[0.88] tracking-tight"
        />
        {finalSubtitle && (
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/70"
          >
            {finalSubtitle}
          </motion.p>
        )}
        {children && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap gap-4"
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default PageHero;
